<template>
  <a-card
      :bordered="true"
      class="domain-disbalance-card"
  >
    <a-tag
        v-if="showTag"
        class="domain-disbalance-card__tag"
        :class="{
          'domain-disbalance-card__tag--current': tagType === 'current',
        }"
    >
      {{ periodLabel }}
    </a-tag>

    <div class="domain-disbalance-card__header">
      <div>
        <h3 class="domain-disbalance-card__title">
          {{ title }}
        </h3>

        <p
            v-if="description"
            class="domain-disbalance-card__description"
        >
          {{ description }}
        </p>
      </div>

      <QuestionCircleOutlined class="domain-disbalance-card__help-icon" />
    </div>

    <div class="domain-disbalance-card__table-scroll">
      <div
          class="domain-disbalance-card__table"
          :class="{
            'domain-disbalance-card__table--grouped': isGroupedMode,
          }"
      >
        <div class="domain-disbalance-card__table-head">
          <span v-if="isGroupedMode">
            {{ groupColumnTitle }}
          </span>

          <span>Домен</span>
          <span>Средний уровень</span>
          <span>Min / max баллы</span>
        </div>

        <template v-if="isGroupedMode">
          <div
              v-for="group in normalizedGroups"
              :key="group.key"
              class="domain-disbalance-card__group"
          >
            <div
                v-for="(row, rowIndex) in group.rows"
                :key="row.key"
                class="domain-disbalance-card__row domain-disbalance-card__row--grouped"
                :class="{
                  'domain-disbalance-card__row--first-in-group': rowIndex === 0,
                  'domain-disbalance-card__row--last-in-group': rowIndex === group.rows.length - 1,
                  'domain-disbalance-card__row--single-in-group': group.rows.length === 1,
                }"
            >
              <span class="domain-disbalance-card__group-label">
                <template v-if="rowIndex === 0">
                  {{ group.label }}
                </template>
              </span>

              <span class="domain-disbalance-card__domain">
                {{ row.label }}
              </span>

              <span
                  class="domain-disbalance-card__average"
                  :class="getAverageClass(row)"
                  :style="getAverageStyle(row)"
              >
                {{ formatValue(row.average) }}
              </span>

              <span class="domain-disbalance-card__range">
                <template v-if="hasVisibleRange(row)">
                  <span
                      v-if="isVisibleValue(row.min)"
                      class="bold"
                  >
                    {{ formatValue(row.min) }}
                  </span>

                  <span v-if="isVisibleValue(row.min) && isVisibleValue(row.max)">
                    /
                  </span>

                  <span v-if="isVisibleValue(row.max)">
                    {{ formatValue(row.max) }}
                  </span>
                </template>
              </span>
            </div>
          </div>
        </template>

        <template v-else>
          <div
              v-for="row in normalizedRows"
              :key="row.key"
              class="domain-disbalance-card__row"
          >
            <span class="domain-disbalance-card__domain">
              {{ row.label }}
            </span>

            <span
                class="domain-disbalance-card__average"
                :class="getAverageClass(row)"
                :style="getAverageStyle(row)"
            >
              {{ formatValue(row.average) }}
            </span>

            <span class="domain-disbalance-card__range">
              <template v-if="hasVisibleRange(row)">
                <span
                    v-if="isVisibleValue(row.min)"
                    class="bold"
                >
                  {{ formatValue(row.min) }}
                </span>

                <span v-if="isVisibleValue(row.min) && isVisibleValue(row.max)">
                  /
                </span>

                <span v-if="isVisibleValue(row.max)">
                  {{ formatValue(row.max) }}
                </span>
              </template>
            </span>
          </div>
        </template>
      </div>
    </div>
  </a-card>
</template>

<script>
import { computed } from 'vue'
import { QuestionCircleOutlined } from '@ant-design/icons-vue'

export default {
  name: 'DomainDisbalanceCard',

  components: {
    QuestionCircleOutlined,
  },

  props: {
    periodLabel: {
      type: String,
      default: 'Было',
    },

    tagType: {
      type: String,
      default: 'previous',
      validator: (value) => ['previous', 'current'].includes(value),
    },

    showTag: {
      type: Boolean,
      default: true,
    },

    title: {
      type: String,
      default: 'Уровень дисбаланса между доменами',
    },

    description: {
      type: String,
      default: '',
    },

    rows: {
      type: Array,
      default: () => [],
    },

    compareRows: {
      type: Array,
      default: () => [],
    },

    groups: {
      type: Array,
      default: () => [],
    },

    groupColumnTitle: {
      type: String,
      default: 'Группа',
    },

    showZeroValues: {
      type: Boolean,
      default: false,
    },

    neutralAverageValue: {
      type: Number,
      default: 0,
    },

    greenMaxAverage: {
      type: Number,
      default: 10,
    },
  },

  setup(props) {
    const isGroupedMode = computed(() => {
      return Array.isArray(props.groups) && props.groups.length > 0
    })

    const compareRowsMap = computed(() => {
      return (Array.isArray(props.compareRows) ? props.compareRows : []).reduce((result, row, index) => {
        const key = row?.key || `row-${index}`

        result[key] = {
          ...row,
          average: toNullableNumber(row?.average),
        }

        return result
      }, {})
    })

    const normalizedRows = computed(() => {
      return (Array.isArray(props.rows) ? props.rows : []).map((row, index) => {
        const key = row?.key || `row-${index}`
        const average = toNullableNumber(row?.average)
        const compareAverage = compareRowsMap.value[key]?.average ?? null
        const min = toNullableNumber(row?.min)
        const max = toNullableNumber(row?.max)

        return {
          ...row,
          key,
          label: row?.label || row?.domain || '',
          average,
          min,
          max,
          compareAverage,
          averageDifference: getDifference(average, compareAverage),
          averageColor: row?.averageColor || row?.color || null,
          averageStatus: row?.averageStatus || null,
        }
      })
    })

    const normalizedGroups = computed(() => {
      return (Array.isArray(props.groups) ? props.groups : []).map((group, groupIndex) => {
        const groupKey = group?.key || `group-${groupIndex}`

        return {
          ...group,
          key: groupKey,
          label: group?.label || '',
          rows: (Array.isArray(group?.rows) ? group.rows : []).map((row, rowIndex) => {
            const key = row?.key || `${groupKey}-row-${rowIndex}`
            const average = toNullableNumber(row?.average)
            const min = toNullableNumber(row?.min)
            const max = toNullableNumber(row?.max)

            return {
              ...row,
              key,
              label: row?.label || row?.domain || '',
              average,
              min,
              max,
              averageColor: row?.averageColor || row?.color || null,
              averageStatus: row?.averageStatus || null,
              averageDifference: null,
            }
          }),
        }
      })
    })

    const getAverageClass = (row) => {
      const number = toNullableNumber(row?.average)

      if (number === null) {
        return {
          'domain-disbalance-card__average--grey': true,
        }
      }

      if (number === props.neutralAverageValue) {
        return {
          'domain-disbalance-card__average--grey': true,
        }
      }

      const status = row?.averageStatus

      if (status) {
        return {
          'domain-disbalance-card__average--grey': status === 'grey' || status === 'neutral',
          'domain-disbalance-card__average--green': status === 'green',
          'domain-disbalance-card__average--red': status === 'red',
        }
      }

      return {
        'domain-disbalance-card__average--green': number > props.neutralAverageValue && number <= props.greenMaxAverage,
        'domain-disbalance-card__average--red': number > props.greenMaxAverage,
      }
    }

    const getAverageStyle = (row) => {
      if (!row?.averageColor) {
        return {}
      }

      return {
        color: row.averageColor,
      }
    }

    const formatValue = (value) => {
      if (!isVisibleValue(value)) {
        return ''
      }

      const number = Number(value)

      return Number.isInteger(number)
          ? number
          : number.toFixed(1)
    }

    const hasVisibleRange = (row) => {
      return isVisibleValue(row?.min) || isVisibleValue(row?.max)
    }

    const isVisibleValue = (value) => {
      if (value === null || value === undefined || value === '') {
        return false
      }

      const number = Number(value)

      if (!Number.isFinite(number)) {
        return false
      }

      return props.showZeroValues || number !== 0
    }

    return {
      isGroupedMode,
      normalizedRows,
      normalizedGroups,
      getAverageClass,
      getAverageStyle,
      formatValue,
      hasVisibleRange,
      isVisibleValue,
    }
  },
}

function getDifference(value, compareValue) {
  if (
      value === null ||
      value === undefined ||
      compareValue === null ||
      compareValue === undefined
  ) {
    return null
  }

  const number = Number(value)
  const compareNumber = Number(compareValue)

  if (!Number.isFinite(number) || !Number.isFinite(compareNumber)) {
    return null
  }

  return number - compareNumber
}

function toNullableNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const number = Number(value)

  return Number.isFinite(number) ? number : null
}
</script>

<style scoped lang="scss">
.domain-disbalance-card {
  position: relative;
  min-width: 0;
  height: 100%;
  border-radius: 8px;
}

.domain-disbalance-card :deep(.ant-card-body) {
  height: 100%;
  padding: 18px 16px 16px;
}

.domain-disbalance-card__tag {
  position: absolute;
  top: -10px;
  left: 20px;
  z-index: 2;
  margin: 0;
  height: 20px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.65);
  border-color: #d5dbe2;
}

.domain-disbalance-card__tag--current {
  color: var(--geek-blue);
  border-color: var(--geek-blue-3);
  background-color: var(--geek-blue-1);
}

.domain-disbalance-card__header {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 28px;
}

.domain-disbalance-card__title {
  margin: 0 0 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.domain-disbalance-card__description {
  max-width: 520px;
  margin: 0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.domain-disbalance-card__help-icon {
  flex: 0 0 auto;
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.25);
  font-size: 14px;
}

.domain-disbalance-card__table-scroll {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.domain-disbalance-card__table {
  width: 100%;
  min-width: 0;
}

.domain-disbalance-card__table-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px 130px;
  gap: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);

  span:last-child {
    text-align: right;
  }
}

.domain-disbalance-card__table--grouped .domain-disbalance-card__table-head {
  grid-template-columns: 100px minmax(0, 1fr) 130px 130px;
}

.domain-disbalance-card__group {
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.domain-disbalance-card__row {
  padding: 8px 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px 130px;
  gap: 14px;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.domain-disbalance-card__table:not(.domain-disbalance-card__table--grouped) .domain-disbalance-card__row:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.domain-disbalance-card__row--grouped {
  grid-template-columns: 100px minmax(0, 1fr) 130px 130px;
  min-height: 32px;
  padding-top: 4px;
  padding-bottom: 4px;
  border-bottom: none;
}

.domain-disbalance-card__row--first-in-group {
  padding-top: 12px;
}

.domain-disbalance-card__row--last-in-group {
  padding-bottom: 12px;
}

.domain-disbalance-card__row--single-in-group {
  padding-top: 12px;
  padding-bottom: 12px;
}

.domain-disbalance-card__group:last-child .domain-disbalance-card__row--last-in-group {
  padding-bottom: 0;
}

.domain-disbalance-card__group:last-child .domain-disbalance-card__row--single-in-group {
  padding-bottom: 0;
}

.domain-disbalance-card__group-label,
.domain-disbalance-card__domain {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.domain-disbalance-card__group-label {
  color: rgba(0, 0, 0, 0.88);
}

.domain-disbalance-card__average {
  min-height: 17px;
  text-align: right;
  font-weight: 600;
}

.domain-disbalance-card__average--grey {
  color: rgba(0, 0, 0, 0.35);
}

.domain-disbalance-card__average--green {
  color: var(--main-green);
}

.domain-disbalance-card__average--red {
  color: #f55565;
}

.domain-disbalance-card__range {
  min-height: 17px;
  text-align: right;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.45);

  .bold {
    font-weight: 600;
    color: rgba(0, 0, 0, 0.88);
  }
}

@media (max-width: $mobile-breakpoint) {
  .domain-disbalance-card__header {
    margin-bottom: 20px;
  }

  .domain-disbalance-card__description {
    max-width: none;
  }

  .domain-disbalance-card__table-head,
  .domain-disbalance-card__row {
    grid-template-columns: minmax(0, 1fr) 84px 86px;
    gap: 8px;
  }

  .domain-disbalance-card__table--grouped .domain-disbalance-card__table-head,
  .domain-disbalance-card__row--grouped {
    grid-template-columns: 82px minmax(0, 1fr) 62px 70px;
    gap: 8px;
  }
}
</style>